import { useState, useEffect, useRef } from "react";
import { Search, MapPin, Loader2, CheckCircle, AlertCircle, TrendingUp, Users, BarChart3, Train, Volume2, Trees, Building2 } from "lucide-react";
import { useDebounce } from "use-debounce";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchAddressSuggestions, estimateReportTime, type AddressSuggestion } from "@/services/api";
import { trackAddressSearch, trackButtonClick, trackAutocompleteSelection } from "@/services/analytics";
import { toast } from "@/hooks/use-toast";

interface PropertySearchProps {
  onSearch: (address: string) => void;
  isLoading: boolean;
}

export const PropertySearch = ({ onSearch, isLoading }: PropertySearchProps) => {
  const [address, setAddress] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [skipFetch, setSkipFetch] = useState(false);
  const [estimatedSeconds, setEstimatedSeconds] = useState(20);
  const [showNoResults, setShowNoResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [debouncedAddress] = useDebounce(address, 300);
  const [debouncedAddressForNoResults] = useDebounce(address, 600);

  // Fetch suggestions when debounced value changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      if (skipFetch) {
        setSkipFetch(false);
        return;
      }

      if (debouncedAddress.trim().length >= 3 && !isLoading) {
        abortControllerRef.current = new AbortController();
        
        setIsLoadingSuggestions(true);
        try {
          const results = await fetchAddressSuggestions(debouncedAddress);
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          setSelectedIndex(-1);
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching suggestions:', error);
          }
        } finally {
          setIsLoadingSuggestions(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedAddress, isLoading, skipFetch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Control when to show "no results" message
  useEffect(() => {
    if (
      debouncedAddressForNoResults.trim().length >= 3 &&
      !isLoading &&
      suggestions.length === 0 &&
      !isLoadingSuggestions &&
      debouncedAddress === debouncedAddressForNoResults
    ) {
      setShowNoResults(true);
    } else {
      setShowNoResults(false);
    }
  }, [debouncedAddressForNoResults, isLoading, suggestions.length, isLoadingSuggestions, debouncedAddress]);

  const parseAddressParts = (addressStr: string) => {
    const parts = addressStr.split(',').map(p => p.trim());
    let suburb: string | undefined;
    let state: string | undefined;
    
    const stateMatch = addressStr.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/);
    state = stateMatch ? stateMatch[1] : undefined;
    
    if (parts.length >= 3) {
      suburb = parts[parts.length - 2]?.replace(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/, '').trim();
    } else if (parts.length === 2) {
      suburb = parts[0];
    }
    
    return { suburb, state };
  };

  const validateAddress = (addr: string): { isValid: boolean; error?: string } => {
    const trimmed = addr.trim();
    
    // Check minimum length
    if (trimmed.length < 10) {
      return { 
        isValid: false, 
        error: "Please enter a complete address (e.g., 123 Smith St, Surry Hills NSW)" 
      };
    }
    
    // Check if address starts with a number (street number required)
    const startsWithNumber = /^\d/.test(trimmed);
    if (!startsWithNumber) {
      return { 
        isValid: false, 
        error: "Please include a street number (e.g., 123 Smith St, not just Smith St)" 
      };
    }
    
    // Check for Australian state (NSW, VIC, QLD, etc.)
    const hasAustralianState = /\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT|New South Wales|Victoria|Queensland|South Australia|Western Australia|Tasmania|Northern Territory|Australian Capital Territory)\b/i.test(trimmed);
    if (!hasAustralianState) {
      return { 
        isValid: false, 
        error: "Please include the state (e.g., NSW, VIC, QLD)" 
      };
    }
    
    return { isValid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    
    // Validate address format
    const validation = validateAddress(address);
    if (!validation.isValid) {
      toast({
        title: "Invalid Address Format",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }
    
    setShowSuggestions(false);
    
    trackButtonClick('get_real_insight', 'property_search');
    
    const { suburb, state } = parseAddressParts(address.trim());
    trackAddressSearch(address.trim(), suburb, state);
    
    const estimate = await estimateReportTime(address.trim());
    setEstimatedSeconds(estimate.estimated_seconds);
    
    onSearch(address.trim());
  };

  const handleSuggestionClick = async (suggestion: AddressSuggestion, index: number) => {
    setAddress(suggestion.display_name);
    setSkipFetch(true);
    setShowSuggestions(false);
    setSuggestions([]);
    
    trackAutocompleteSelection(suggestion.display_name, index);
    
    trackAddressSearch(
      suggestion.display_name,
      suggestion.address_parts.suburb,
      suggestion.address_parts.state
    );
    
    const estimate = await estimateReportTime(suggestion.display_name);
    setEstimatedSeconds(estimate.estimated_seconds);
    
    onSearch(suggestion.display_name);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex], selectedIndex);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Animated counter for reports generated
  const [displayCount, setDisplayCount] = useState(0);
  const targetCount = 12847;
  
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = targetCount / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= targetCount) {
        setDisplayCount(targetCount);
        clearInterval(timer);
      } else {
        setDisplayCount(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8 px-4">
      {/* Hero Section */}
      <div className="text-center space-y-4 sm:space-y-5">
        {/* Pill badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/20">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          <span className="text-xs sm:text-sm font-semibold text-primary">
            {displayCount.toLocaleString()}+ reports generated
          </span>
        </div>
        
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight px-2 animate-fade-in">
          Know Before You Buy
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
          Get the <span className="font-semibold text-primary">honest truth</span> about any Australian property. Real data. No fluff. Zero BS.
        </p>
        
        {/* City coverage badges */}
        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
          <Badge variant="outline" className="bg-background text-xs font-medium">🏙️ Sydney</Badge>
          <Badge variant="outline" className="bg-background text-xs font-medium">🏙️ Melbourne</Badge>
          <Badge variant="outline" className="bg-background text-xs font-medium">🏙️ Brisbane</Badge>
          <Badge variant="outline" className="bg-background text-xs font-medium">🏙️ Perth</Badge>
          <Badge variant="outline" className="bg-background text-xs font-medium">🏙️ Adelaide</Badge>
        </div>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4 sm:h-5 sm:w-5 z-10" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="123 Smith St, Surry Hills NSW"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setShowNoResults(false);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            className="pl-10 sm:pl-12 pr-3 sm:pr-4 h-12 sm:h-14 md:h-16 text-sm sm:text-base md:text-lg rounded-lg sm:rounded-xl shadow-lg border-2 focus:border-primary transition-all placeholder:text-muted-foreground/60"
            disabled={isLoading}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="text"
            name="property-address-search"
            id="property-address-search"
          />
          
          {/* Autocomplete Dropdown */}
          {showSuggestions && suggestions.length > 0 && !isLoading && (
            <div
              ref={dropdownRef}
              className="absolute z-50 w-full mt-2 bg-background border-2 border-border rounded-xl shadow-xl max-h-80 overflow-y-auto"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion, index)}
                  className={`w-full px-4 py-3 text-left hover:bg-accent transition-colors flex items-start gap-3 border-b border-border last:border-b-0 ${
                    index === selectedIndex ? "bg-accent" : ""
                  }`}
                >
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {suggestion.display_name}
                    </div>
                    {suggestion.address_parts.suburb && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {[
                          suggestion.address_parts.suburb,
                          suggestion.address_parts.state,
                          suggestion.address_parts.postcode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {/* Helper messages */}
          {!isLoading && address.trim().length > 0 && address.trim().length < 3 && (
            <div className="absolute z-50 w-full mt-2 px-4 py-3 bg-muted/50 border border-border rounded-xl">
              <p className="text-sm text-muted-foreground">
                Type at least 3 characters to search...
              </p>
            </div>
          )}
          
          {showNoResults && (
            <div className="absolute z-50 w-full mt-2 px-4 py-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <p className="text-sm text-blue-700 dark:text-blue-400 flex items-start gap-2">
                <span className="flex-shrink-0">💡</span>
                <span>
                  {/^\d/.test(address.trim()) 
                    ? "Still searching... Try adding more details like the suburb and state (e.g., '123 Smith St, Surry Hills NSW')."
                    : "Tip: Start with a street number for better results (e.g., '123 Smith Street' not just 'Smith Street')."}
                </span>
              </p>
            </div>
          )}
        </div>
        
        <Button
          type="submit"
          size="lg"
          disabled={!address.trim() || isLoading}
          className="w-full mt-3 sm:mt-4 h-12 sm:h-14 md:h-16 text-sm sm:text-base md:text-lg font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
              <span className="animate-pulse">Crunching the data</span>
              <span className="hidden sm:flex gap-1">
                <span className="animate-bounce" style={{ animationDelay: "0ms" }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: "150ms" }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: "300ms" }}>.</span>
              </span>
            </div>
          ) : (
            <span className="hidden sm:inline">Get My Free Property Report</span>
          )}
          {!isLoading && (
            <span className="sm:hidden">Get Free Report</span>
          )}
        </Button>
        
        {isLoading && (
          <div className="mt-3 text-center">
            <p className="text-sm text-muted-foreground animate-pulse">
              Gathering data from Census, crime stats, transport, and more... (~{estimatedSeconds}s)
            </p>
          </div>
        )}
      </form>

      {/* Social Proof */}
      {!isLoading && (
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
            <span className="whitespace-nowrap">Free forever</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
            <span className="whitespace-nowrap">Real data</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
            <span className="whitespace-nowrap">~20 sec results</span>
          </div>
        </div>
      )}

      {/* What You'll Get Section - Only show when not loading */}
      {!isLoading && (
        <>
          <div className="mt-12 pt-8 border-t border-border">
            <div className="text-center space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">What You'll Get</h2>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                Your comprehensive property report analyzes the neighborhood across 7 key categories
              </p>
              
              {/* Feature Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8 text-left">
                <div className="group p-5 rounded-xl border border-border bg-card hover:shadow-lg hover:scale-[1.02] hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Train className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">Transport & Access</h3>
                      <p className="text-sm text-muted-foreground">How easy is your commute? Train frequency, bus routes, CBD access time</p>
                    </div>
                  </div>
                </div>

                <div className="group p-5 rounded-xl border border-border bg-card hover:shadow-lg hover:scale-[1.02] hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Volume2 className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">Noise & Traffic</h3>
                      <p className="text-sm text-muted-foreground">Will you hear traffic all day? Major roads within 300m, noise impact</p>
                    </div>
                  </div>
                </div>

                <div className="group p-5 rounded-xl border border-border bg-card hover:shadow-lg hover:scale-[1.02] hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Trees className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">Parks & Lifestyle</h3>
                      <p className="text-sm text-muted-foreground">Green space nearby? Major parks, cafes, restaurants, walkability</p>
                    </div>
                  </div>
                </div>

                <div className="group p-5 rounded-xl border border-border bg-card hover:shadow-lg hover:scale-[1.02] hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">Demographics</h3>
                      <p className="text-sm text-muted-foreground">Who are your neighbors? Age, income, education, crime stats</p>
                    </div>
                  </div>
                </div>

                <div className="group p-5 rounded-xl border border-border bg-card hover:shadow-lg hover:scale-[1.02] hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <BarChart3 className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">Property Market</h3>
                      <p className="text-sm text-muted-foreground">What's it worth? Recent sales, price trends, days on market</p>
                    </div>
                  </div>
                </div>

                <div className="group p-5 rounded-xl border border-border bg-card hover:shadow-lg hover:scale-[1.02] hover:border-primary/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Building2 className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">Development</h3>
                      <p className="text-sm text-muted-foreground">What's being built? New approvals, gentrification index</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bonus Features */}
              <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                <h3 className="text-lg font-bold text-foreground mb-3">Plus, You'll Get Honest Insights For:</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-foreground">Investors</p>
                      <p className="text-sm text-muted-foreground">Rental yield potential, tenant appeal, capital growth outlook</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-foreground">Owner-Occupiers</p>
                      <p className="text-sm text-muted-foreground">Livability score, noise tolerance needed, lifestyle fit</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="text-center space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">How It Works</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                <div className="relative group">
                  <div className="space-y-4 p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                      <span className="text-2xl font-bold text-primary">1</span>
                    </div>
                    <h3 className="font-semibold text-foreground text-lg">Enter Address</h3>
                    <p className="text-sm text-muted-foreground">Type any Australian property address</p>
                  </div>
                  {/* Connector line (desktop only) */}
                  <div className="hidden sm:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary/30 to-transparent" />
                </div>
                <div className="relative group">
                  <div className="space-y-4 p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                      <span className="text-2xl font-bold text-primary">2</span>
                    </div>
                    <h3 className="font-semibold text-foreground text-lg">We Analyze</h3>
                    <p className="text-sm text-muted-foreground">We pull real data from Census, transport, crime stats, property sales</p>
                  </div>
                  {/* Connector line (desktop only) */}
                  <div className="hidden sm:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-primary/30 to-transparent" />
                </div>
                <div className="group">
                  <div className="space-y-4 p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                      <span className="text-2xl font-bold text-primary">3</span>
                    </div>
                    <h3 className="font-semibold text-foreground text-lg">Get The Truth</h3>
                    <p className="text-sm text-muted-foreground">Receive your comprehensive report in ~20 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sample Reports CTA */}
          <div className="mt-8 p-6 rounded-xl bg-muted/50 border border-border text-center">
            <p className="text-base text-muted-foreground mb-4">
              <strong className="text-foreground">New to property research?</strong> Check out sample reports to see exactly what you'll get
            </p>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => window.location.href = "/sample-reports"}
              className="font-semibold"
            >
              View Sample Reports →
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              <strong className="text-foreground">Real Data Sources:</strong> Census 2021, SEIFA indexes, property sales data, crime statistics, building approvals, HERE APIs (real-time transit, places, routing), OpenStreetMap, and ChatGPT for AI analysis. Our tone is cheeky, but the data is legit.
              <br /><br />
              <strong className="text-foreground">Disclaimer:</strong> This is research, not financial advice. Always do your due diligence and consult professionals before making property decisions.
            </p>
          </div>
        </>
      )}
    </div>
  );
};
